import type { AssistantSessionSummary } from '@/lib/api/assistant.api'
import { apiClient } from '@/lib/api/api-client'
import { assistantApi } from '@/lib/api/assistant.api'

import type {
  AssistantChatStreamEvent,
  AssistantMessagesPage,
} from '@scrambl/shared'

/** What the portal's Benny can do for this client, from the server's filtered tool list. */
export interface ClientPortalAssistantCapabilities {
  enabled: boolean
  suggestedAsks: string[]
  actions: {
    createTicket: boolean
    replyTicket: boolean
    closeTicket: boolean
    commentOnTask: boolean
    updateTaskStatus: boolean
  }
}

interface StreamHandlers {
  signal: AbortSignal
  onEvent: (event: AssistantChatStreamEvent) => void
}

const base = (entityId: string) =>
  `/client-portal/entities/${encodeURIComponent(entityId)}/assistant`

/**
 * Benny inside a client portal.
 *
 * Every conversation belongs to the signed-in client user: the server scopes
 * each session to the organization, the client entity and the user, so two
 * people at the same client never see each other's threads. Responses use the
 * org assistant's envelopes and SSE vocabulary, so streaming reuses its parser.
 */
export class ClientPortalAssistantApi {
  private static instance: ClientPortalAssistantApi | null = null

  static getInstance() {
    return (this.instance ??= new ClientPortalAssistantApi())
  }

  async getCapabilities(
    entityId: string,
  ): Promise<ClientPortalAssistantCapabilities> {
    const response = await apiClient<{
      data: ClientPortalAssistantCapabilities
    }>(`${base(entityId)}/capabilities`)
    return response.data
  }

  async listSessions(
    entityId: string,
    limit = 50,
  ): Promise<AssistantSessionSummary[]> {
    const response = await apiClient<{ data: AssistantSessionSummary[] }>(
      `${base(entityId)}/sessions?limit=${limit}`,
    )
    return response.data
  }

  async getMessages(
    entityId: string,
    sessionId: string,
    options: { cursor?: string; limit?: number } = {},
  ): Promise<AssistantMessagesPage> {
    const params = new URLSearchParams()
    if (options.cursor) params.set('cursor', options.cursor)
    if (options.limit) params.set('limit', String(options.limit))
    const query = params.size > 0 ? `?${params}` : ''
    const response = await apiClient<{ data: AssistantMessagesPage }>(
      `${base(entityId)}/sessions/${encodeURIComponent(sessionId)}/messages${query}`,
    )
    return response.data
  }

  async renameSession(
    entityId: string,
    sessionId: string,
    title: string,
  ): Promise<void> {
    await apiClient(
      `${base(entityId)}/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'PATCH', body: { title } },
    )
  }

  async deleteSession(entityId: string, sessionId: string): Promise<void> {
    await apiClient(
      `${base(entityId)}/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'DELETE' },
    )
  }

  async chatStream(
    entityId: string,
    request: { message: string; sessionId?: string },
    handlers: StreamHandlers,
  ): Promise<void> {
    await assistantApi.streamEvents(
      `${base(entityId)}/chat/stream`,
      request,
      handlers,
    )
  }

  async confirmToolCalls(
    entityId: string,
    sessionId: string,
    body: { toolCallIds: string[]; approve: boolean },
    handlers: StreamHandlers,
  ): Promise<void> {
    await assistantApi.streamEvents(
      `${base(entityId)}/sessions/${encodeURIComponent(sessionId)}/confirm`,
      body,
      handlers,
    )
  }
}

export const clientPortalAssistantApi = ClientPortalAssistantApi.getInstance()
